import { makeAutoObservable } from "mobx";
import { NovoProduto, Produto } from "../types/store";
import { PedidoResumo } from "../pages/AdminPedidos";
import { descricaoByState } from "../pages/OrderDetails";
import { API_BASE_URL } from "../config";

class AdminStore {
  private _accessToken: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  set accessToken(token: string | null) {
    this._accessToken = token;
  }

  get accessToken() {
    return this._accessToken;
  }

  get isLoggedIn() {
    return !!this._accessToken;
  }

  async handleLogin(login: string, password: string) {
    try {
      const res = await fetch(`${API_BASE_URL}/gerenciar/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // if your backend uses cookies/session
        body: JSON.stringify({ login, password }),
      });

      if (!res.ok) {
        throw new Error("Login inválido");
      }

      const data = await res.json();
      this.accessToken = data.accessToken;
      return true;
    } catch (error) {
      this.accessToken = null;
      return false;
    }
  }

  async logout() {
    try {
      await fetch(`${API_BASE_URL}/gerenciar/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      // ignore errors
    }
    this.accessToken = null;
  }

  async cadastraProduto(produto: NovoProduto) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/produtos/cadastraProduto`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
          credentials: "include", // if your backend uses cookies/session
          body: JSON.stringify(produto),
        }
      );

      if (!res.ok) {
        throw new Error("Erro ao cadastrar produto");
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  async atualizaProduto(produto: Produto) {
    try {
      const res = await fetch(`${API_BASE_URL}/produtos/updateProduct`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
        credentials: "include", // if your backend uses cookies/session
        body: JSON.stringify(produto),
      });

      if (!res.ok) {
        throw new Error("Erro ao cadastrar produto");
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Busca lista resumida de produtos (id, nome, categoria, ativo)
   */
  async getProductList(): Promise<
    Pick<Produto, "id" | "nome" | "categoria" | "ativo" | "quantidadeEstoque">[]
  > {
    try {
      const res = await fetch(`${API_BASE_URL}/produtos/listProducts`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: this.accessToken ? `Bearer ${this.accessToken}` : "",
        },
      });
      if (!res.ok) {
        throw new Error("Erro ao buscar lista de produtos");
      }
      const data = await res.json();
      // Garante que só retorna os campos desejados
      return Array.isArray(data)
        ? data.map((item) => ({
          id: item.id,
          nome: item.nome,
          categoria: item.categoria,
          ativo: item.ativo,
          quantidadeEstoque: item.quantidadeEstoque,
        }))
        : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Busca lista resumida de produtos (id, nome, categoria, ativo)
   */
  async getPedidoList(): Promise<PedidoResumo[]> {
    try {
      const res = await fetch(`${API_BASE_URL}/pedidos/listPedidos`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: this.accessToken ? `Bearer ${this.accessToken}` : "",
        },
      });
      if (!res.ok) {
        throw new Error("Erro ao buscar lista de pedidos");
      }
      const data = await res.json();
      // Garante que só retorna os campos desejados
      return Array.isArray(data)
        ? data.map((item) => ({
          id: item.id,
          cliente_email: item.cliente_email,
          cliente_nome: item.cliente_nome,
          status: item.pedido_status_nome,
          total: item.preco_total,
          frete: item.preco_frete,
        }))
        : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Busca dados de envio de um pedido pelo id
   */
  async getPedidoDadosEnvio(id: number): Promise<{
    cliente_cep: string;
    cliente_endereco: string;
    cliente_numero_casa: string;
    cliente_complemento: string;
    cliente_estado: string;
    forma_envio_id: number;
    forma_envio_nome: string;
  } | null> {
    try {
      const res = await fetch(
        `${API_BASE_URL}/pedidos/getPedidoDadosEnvio/${id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: this.accessToken ? `Bearer ${this.accessToken}` : "",
          },
        }
      );
      if (!res.ok) {
        throw new Error("Erro ao buscar dados de envio do pedido");
      }
      const data = await res.json();
      return {
        cliente_cep: data.cliente_cep,
        cliente_endereco: data.cliente_endereco,
        cliente_numero_casa: data.cliente_numero_casa,
        cliente_complemento: data.cliente_complemento,
        cliente_estado: data.cliente_estado,
        forma_envio_id: data.forma_envio_id,
        forma_envio_nome: data.forma_envio_nome,
      };
    } catch (error) {
      return null;
    }
  }


  /**
   * Busca dados dos itens do pedido
   */
  async getPedidoDadosItens(id: number): Promise<Array<{
    produto_nome: string;
    quantidade: number;
    desconto: number;
    preco: number;
  }>> {
    try {
      const res = await fetch(`${API_BASE_URL}/pedidos/getPedidoDadosItens/${id}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: this.accessToken ? `Bearer ${this.accessToken}` : "",
        },
      });
      if (!res.ok) {
        throw new Error("Erro ao buscar dados dos itens do pedido");
      }
      const data = await res.json();
      return Array.isArray(data) ? data.map((item) => ({
        produto_nome: item.produto_nome,
        quantidade: item.quantidade,
        desconto: item.desconto,
        preco: Number(item.total),
      })) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Busca atualizações do pedido
   */
  async getPedidoAtualizacoes(id: number): Promise<Array<{
    pedido_status: string;
    date: string | null;
    concluido: boolean;
  }>> {
    try {
      const res = await fetch(`${API_BASE_URL}/pedidos/getPedidoAtualizacoes/${id}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: this.accessToken ? `Bearer ${this.accessToken}` : "",
        },
      });
      if (!res.ok) {
        throw new Error("Erro ao buscar atualizações do pedido");
      }
      const data = await res.json();
      return Array.isArray(data) ? data.map((update) => ({
        pedido_status: descricaoByState[update.pedido_status],
        date: update.date,
        concluido: update.concluido,
      })) : [];
    } catch (error) {
      console.error("Erro ao buscar atualizações do pedido:", error);
      return [];
    }
  }

  /**
   * Atualiza o status do pedido
   * @param pedidoId id do pedido
   * @param statusUpdates array de objetos { id: number, concluido: boolean }
   */
  async atualizarPedidoStatus(
    pedidoId: number,
    statusUpdates: Array<{ id: number; concluido: boolean }>
  ): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE_URL}/pedidos/atualizarPedidoStatus`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: this.accessToken ? `Bearer ${this.accessToken}` : "",
        },
        body: JSON.stringify({
          pedidoId,
          statusUpdates,
        }),
      });
      if (!res.ok) {
        throw new Error("Erro ao atualizar status do pedido");
      }
      return true;
    } catch (error) {
      console.error("Erro ao atualizar status do pedido:", error);
      return false;
    }
  }
}

export const adminStore = new AdminStore();
